import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Backend API URL for config endpoint
 */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Custom hook for Native Google Sign-In using Google Identity Services (GIS)
 * Provides popup-based authentication without page redirects
 * Fetches Google Client ID securely from backend
 */
export const useGoogleAuth = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [googleClientId, setGoogleClientId] = useState(null);
    const initAttempted = useRef(false);
    const configFetched = useRef(false);

    // Fetch Google Client ID from backend config endpoint
    useEffect(() => {
        if (configFetched.current) return;
        configFetched.current = true;
        
        const fetchConfig = async () => {
            try {
                const response = await fetch(`${API_URL}/api/config`);
                if (response.ok) {
                    const config = await response.json();
                    setGoogleClientId(config.google_client_id);
                    console.log('✨ Fetched Google Client ID from backend');
                } else {
                    console.error('Failed to fetch config');
                    setError('Failed to load authentication configuration');
                }
            } catch (err) {
                console.error('Config fetch error:', err);
                setError('Failed to load authentication configuration');
            }
        };
        
        fetchConfig();
    }, []);

    // Initialize Google Identity Services after client ID is available
    useEffect(() => {
        if (initAttempted.current || !googleClientId) return;
        initAttempted.current = true;

        const initializeGIS = () => {
            if (window.google?.accounts?.id) {
                try {
                    window.google.accounts.id.initialize({
                        client_id: googleClientId,
                        callback: handleCredentialResponse,
                        auto_select: false,
                        cancel_on_tap_outside: true,
                    });
                    setIsInitialized(true);
                    console.log('✨ Google Identity Services initialized');
                } catch (err) {
                    console.error('GIS init error:', err);
                    setError('Failed to initialize Google Sign-In');
                }
            }
        };

        // Try immediately, then retry with delays
        if (window.google?.accounts?.id) {
            initializeGIS();
        } else {
            const timers = [100, 500, 1000, 2000].map(delay =>
                setTimeout(initializeGIS, delay)
            );
            return () => timers.forEach(clearTimeout);
        }
    }, [googleClientId]);

    // Handle credential response from Google
    const handleCredentialResponse = useCallback(async (response) => {
        setIsLoading(true);
        setError(null);

        try {
            const credential = response.credential;
            
            // Send ID token to backend for verification
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const verifyResponse = await fetch(`${backendUrl}/api/auth/google`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ id_token: credential }),
            });

            if (!verifyResponse.ok) {
                const errorData = await verifyResponse.json();
                throw new Error(errorData.detail || 'Authentication failed');
            }

            const userData = await verifyResponse.json();
            setUser(userData);
            
            // Store in localStorage for persistence
            localStorage.setItem('astral_user', JSON.stringify(userData));
            
            // Store session token for API calls
            if (userData.session_token) {
                localStorage.setItem('session_token', userData.session_token);
            }
            
            return userData;
        } catch (err) {
            console.error('Google auth error:', err);
            setError(err.message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Trigger Google Sign-In popup
    const signIn = useCallback(() => {
        if (!isInitialized || !googleClientId) {
            setError('Google Sign-In not ready. Please try again.');
            return Promise.reject(new Error('Not initialized'));
        }

        return new Promise((resolve, reject) => {
            try {
                // Store resolve/reject for the callback
                window._googleAuthResolve = resolve;
                window._googleAuthReject = reject;

                // Override callback temporarily to handle promise
                window.google.accounts.id.initialize({
                    client_id: googleClientId,
                    callback: async (response) => {
                        try {
                            const userData = await handleCredentialResponse(response);
                            window._googleAuthResolve?.(userData);
                        } catch (err) {
                            window._googleAuthReject?.(err);
                        }
                    },
                    auto_select: false,
                    cancel_on_tap_outside: true,
                });

                // Trigger the Google One Tap / Account Chooser
                window.google.accounts.id.prompt((notification) => {
                    if (notification.isNotDisplayed()) {
                        // One Tap not displayed, try alternative
                        const reason = notification.getNotDisplayedReason();
                        console.log('One Tap not displayed:', reason);
                        
                        // Fall back to rendering a temporary button and clicking it
                        renderAndClickButton(resolve, reject);
                    } else if (notification.isSkippedMoment()) {
                        console.log('One Tap skipped');
                        renderAndClickButton(resolve, reject);
                    } else if (notification.isDismissedMoment()) {
                        const reason = notification.getDismissedReason();
                        if (reason === 'credential_returned') {
                            // Success - callback will handle it
                        } else {
                            reject(new Error('Sign-in dismissed'));
                        }
                    }
                });
            } catch (err) {
                reject(err);
            }
        });
    }, [isInitialized, googleClientId, handleCredentialResponse]);

    // Fallback: render a hidden button and trigger click
    const renderAndClickButton = (resolve, reject) => {
        // Create temporary container
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'fixed';
        tempDiv.style.top = '50%';
        tempDiv.style.left = '50%';
        tempDiv.style.transform = 'translate(-50%, -50%)';
        tempDiv.style.zIndex = '99999';
        tempDiv.id = 'temp-google-btn';
        document.body.appendChild(tempDiv);

        window.google.accounts.id.renderButton(tempDiv, {
            type: 'standard',
            theme: 'filled_black',
            size: 'large',
            text: 'continue_with',
            shape: 'rectangular',
        });

        // Click the rendered button after a brief delay
        setTimeout(() => {
            const btn = tempDiv.querySelector('div[role="button"]');
            if (btn) {
                btn.click();
            }
            // Remove temp container after interaction
            setTimeout(() => {
                tempDiv.remove();
            }, 100);
        }, 100);
    };

    // Sign out
    const signOut = useCallback(async () => {
        try {
            // Revoke Google session
            if (window.google?.accounts?.id) {
                window.google.accounts.id.disableAutoSelect();
            }

            // Clear backend session
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            await fetch(`${backendUrl}/api/auth/logout`, {
                method: 'POST',
                credentials: 'include',
            });

            // Clear local storage
            localStorage.removeItem('astral_user');
            setUser(null);
        } catch (err) {
            console.error('Sign out error:', err);
        }
    }, []);

    // Check for existing session on mount
    useEffect(() => {
        const checkSession = async () => {
            // GUARD: Only check session if we have a token
            // This prevents unnecessary 401 errors for guest users
            const storedToken = localStorage.getItem('session_token');
            if (!storedToken) {
                console.log('🔐 useGoogleAuth: No session token, skipping /api/auth/me check');
                return;
            }
            
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                
                // Build headers with session token
                const headers = {
                    'Authorization': `Bearer ${storedToken}`
                };
                
                const response = await fetch(`${backendUrl}/api/auth/me`, {
                    credentials: 'include',
                    headers
                });
                
                if (response.ok) {
                    const userData = await response.json();
                    setUser(userData);
                    localStorage.setItem('astral_user', JSON.stringify(userData));
                    
                    // Store session token if returned
                    if (userData.session_token) {
                        localStorage.setItem('session_token', userData.session_token);
                    }
                } else {
                    // Session invalid - clear localStorage
                    console.log('⚠️ useGoogleAuth: Session token invalid, clearing...');
                    localStorage.removeItem('astral_user');
                    localStorage.removeItem('session_token');
                }
            } catch (err) {
                console.warn('useGoogleAuth: Session check error:', err);
            }
        };

        // Also check localStorage first for instant UI
        const storedUser = localStorage.getItem('astral_user');
        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
            } catch (e) {
                localStorage.removeItem('astral_user');
            }
        }

        checkSession();
    }, []);

    return {
        signIn,
        signOut,
        user,
        isLoading,
        error,
        isInitialized,
    };
};

export default useGoogleAuth;
